import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import api from "../lib/api";
import { reportError } from "../lib/errorReporter";
import {
  LIFECYCLE_FILTERS,
  PAGE_SIZE,
  deliveryErrorKey,
  platformErrorMessage,
} from "../lib/platformValidation";
import PlatformShell from "../components/platform/PlatformShell";
import PlatformOrgTable from "../components/platform/PlatformOrgTable";
import PlatformLifecycleFilter from "../components/platform/PlatformLifecycleFilter";
import PlatformOrgCreateDialog from "../components/platform/PlatformOrgCreateDialog";

/**
 * Platform console — organization list.
 *
 * Search term, lifecycle filter and page offset live in the URL, so a platform
 * administrator can bookmark or share a position in the list. Every request is
 * sent WITHOUT an organization header: this screen is not scoped to a tenant.
 *
 * Creation is a two-step story on purpose (Phase 2D.1C): the organization plus
 * its single pending Owner invitation are committed first, and the invitation
 * e-mail is attempted afterwards. That is why a successful creation can still
 * report that the e-mail did not go out — with a working resend on the detail
 * screen — instead of pretending the tenant is ready.
 */
export default function PlatformAdminOrganizations() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("search") || "";
  const requested = searchParams.get("lifecycle") || "all";
  const lifecycle = LIFECYCLE_FILTERS.includes(requested) ? requested : "all";
  const offset = Math.max(0, parseInt(searchParams.get("offset") || "0", 10) || 0);

  const [term, setTerm] = useState(search);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState("");
  const [warning, setWarning] = useState("");
  // A purged organization no longer exists, so its detail screen cannot report
  // the outcome. The confirmation travels with the navigation instead.
  const [flash, setFlash] = useState(() => location.state?.flash || null);

  useEffect(() => setTerm(search), [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/platform-admin/organizations", {
        params: {
          search: search || undefined,
          lifecycle: lifecycle === "all" ? undefined : lifecycle,
          limit: PAGE_SIZE,
          offset,
        },
        skipOrgHeader: true,
      });
      setData(r.data);
    } catch (err) {
      reportError("platform.listOrganizations", err);
      setError(platformErrorMessage(err, t("common.error_generic")));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [lifecycle, offset, search, t]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = (nextSearch, nextLifecycle, nextOffset) => {
    const params = new URLSearchParams();
    if (nextSearch) params.set("search", nextSearch);
    if (nextLifecycle && nextLifecycle !== "all") params.set("lifecycle", nextLifecycle);
    if (nextOffset > 0) params.set("offset", String(nextOffset));
    setSearchParams(params, { replace: false });
  };

  const created = (payload) => {
    setCreating(false);
    const delivery = payload?.delivery;
    const flash =
      delivery && delivery.delivered === false
        ? {
            tone: "warning",
            text: `${t("platform.created_delivery_failed")} ${t(deliveryErrorKey(delivery.error_code))}`,
          }
        : { tone: "success", text: t("platform.created") };
    const id = payload?.organization?.id;
    if (id) {
      // The detail screen is where the invitation can be resent, so the outcome
      // travels with the navigation instead of being lost on this page.
      nav(`/platform-admin/organizations/${id}`, { state: { flash } });
      return;
    }
    if (flash.tone === "warning") {
      setNotice("");
      setWarning(flash.text);
    } else {
      setWarning("");
      setNotice(flash.text);
    }
    load();
  };

  const items = data?.items || [];
  const total = data?.total || 0;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + items.length, total);

  return (
    <PlatformShell
      title={t("platform.organizations")}
      actions={
        <>
          <button
            type="button"
            className="qw-btn primary sm"
            onClick={() => {
              setNotice("");
              setWarning("");
              setCreating(true);
            }}
            data-testid="platform-org-create-open"
          >
            <Plus size={14} strokeWidth={2} aria-hidden="true" />
            {t("platform.new_organization")}
          </button>
          <button
            type="button"
            className="qw-btn ghost sm"
            onClick={load}
            disabled={loading}
            data-testid="platform-org-refresh"
          >
            {t("platform.refresh")}
          </button>
        </>
      }
    >
      <form
        className="hstack"
        style={{ marginBottom: 12 }}
        data-testid="platform-org-search-form"
        onSubmit={(e) => {
          e.preventDefault();
          apply(term.trim(), lifecycle, 0);
        }}
      >
        <input
          className="qw-input"
          style={{ maxWidth: 320 }}
          placeholder={t("platform.search_placeholder")}
          aria-label={t("platform.search_placeholder")}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          maxLength={200}
          data-testid="platform-org-search"
        />
        <button type="submit" className="qw-btn primary sm" data-testid="platform-org-search-submit">
          {t("platform.search")}
        </button>
        {search && (
          <button
            type="button"
            className="qw-btn ghost sm"
            onClick={() => apply("", lifecycle, 0)}
            data-testid="platform-org-search-clear"
          >
            {t("platform.clear_search")}
          </button>
        )}
      </form>

      <PlatformLifecycleFilter
        value={lifecycle}
        disabled={loading}
        onChange={(next) => apply(search, next, 0)}
      />

      {flash && (
        <div
          className={flash.tone === "warning" ? "qw-error" : "qw-help"}
          style={
            flash.tone === "warning"
              ? { marginTop: 10 }
              : { color: "#059669", marginTop: 10 }
          }
          data-testid="platform-list-flash"
        >
          {flash.text}
          <button
            type="button"
            className="qw-btn ghost sm"
            style={{ marginLeft: 8 }}
            onClick={() => setFlash(null)}
            data-testid="platform-list-flash-dismiss"
          >
            {t("platform.dismiss")}
          </button>
        </div>
      )}

      {notice && (
        <div
          className="qw-help"
          style={{ color: "#059669", marginTop: 10 }}
          data-testid="platform-org-created-notice"
        >
          {notice}
        </div>
      )}
      {warning && (
        <div className="qw-error" style={{ marginTop: 10 }} data-testid="platform-org-created-warning">
          {warning}
        </div>
      )}

      <div style={{ height: 12 }} />

      {loading ? (
        <div className="qw-empty" data-testid="platform-org-loading">
          {t("common.loading")}
        </div>
      ) : error ? (
        <div className="qw-card qw-empty" data-testid="platform-org-error">
          {error}
        </div>
      ) : items.length === 0 ? (
        <div className="qw-card qw-empty" data-testid="platform-org-empty">
          {t("platform.empty")}
        </div>
      ) : (
        <>
          <PlatformOrgTable items={items} />
          <div className="hstack" style={{ marginTop: 12 }} data-testid="platform-pagination">
            <button
              type="button"
              className="qw-btn ghost sm"
              disabled={offset === 0}
              onClick={() => apply(search, lifecycle, Math.max(0, offset - PAGE_SIZE))}
              data-testid="platform-page-prev"
            >
              {t("platform.prev")}
            </button>
            <span className="qw-help" data-testid="platform-page-info">
              {t("platform.page_info", { from, to, total })}
            </span>
            <button
              type="button"
              className="qw-btn ghost sm"
              disabled={to >= total}
              onClick={() => apply(search, lifecycle, offset + PAGE_SIZE)}
              data-testid="platform-page-next"
            >
              {t("platform.next")}
            </button>
          </div>
        </>
      )}

      <PlatformOrgCreateDialog
        open={creating}
        onCancel={() => setCreating(false)}
        onCreated={created}
      />
    </PlatformShell>
  );
}
